// Canonical event-name constants the dashboard explicitly references.
// The wider event vocabulary lives in /tracking/events/ and is fetched at runtime.
// Hooks should `enabled`-guard each fetch on whether the name is actually present.

export const EventNames = {
	SESSION_START: "SESSION_START",
	SOCIAL_POST_SEEN: "SOCIAL_POST_SEEN",
	SOCIAL_POST_REACTED: "SOCIAL_POST_REACTED",
	VIDEO_PLAYED: "VIDEO_PLAYED",
	NOTIFICATION_DELIVERED: "NOTIFICATION_DELIVERED",
	NOTIFICATION_OPENED: "NOTIFICATION_OPENED",
	ANNOUNCEMENT_READ: "ANNOUNCEMENT_READ",
	FORM_OPENED: "FORM_OPENED",
	FORM_STARTED: "FORM_STARTED",
	FORM_SUBMITTED: "FORM_SUBMITTED"
} as const;

// All trackable surfaces. `CONTENT_SURFACES` is a curated subset shown
// in the Content tab — ANNOUNCEMENT is intentionally excluded there
// because announcement metrics live in Social & Comms instead.
export type TrackableSurface =
	| "CAROUSEL"
	| "PROMOTION"
	| "KEY_SHOUT"
	| "QUICK_LINK"
	| "ANNOUNCEMENT"
	| "NEWS"
	| "EVENT"
	| "TAKEOVER";

export type ContentSurface = Exclude<TrackableSurface, "ANNOUNCEMENT">;

export const CONTENT_SURFACES: { key: ContentSurface; label: string }[] = [
	{ key: "CAROUSEL", label: "Carousel" },
	{ key: "PROMOTION", label: "Promotions" },
	{ key: "KEY_SHOUT", label: "Key Shouts" },
	{ key: "QUICK_LINK", label: "Quick Links" },
	{ key: "NEWS", label: "News" },
	{ key: "EVENT", label: "Events" },
	{ key: "TAKEOVER", label: "Takeovers" }
];

export const impressionEvent = (surface: TrackableSurface): string =>
	`${surface}_IMPRESSION`;

export const clickEvent = (surface: TrackableSurface): string =>
	`${surface}_CLICK`;

const ALL_SURFACES: TrackableSurface[] = [
	"CAROUSEL",
	"PROMOTION",
	"KEY_SHOUT",
	"QUICK_LINK",
	"ANNOUNCEMENT",
	"NEWS",
	"EVENT",
	"TAKEOVER"
];

/**
 * Extracts the surface (e.g. KEY_SHOUT) from a tracking event name
 * (KEY_SHOUT_IMPRESSION). Returns null for events that don't carry
 * an entity_id we know how to resolve (SESSION_START, etc.).
 */
export const surfaceForEvent = (
	eventName: string | null | undefined
): TrackableSurface | null => {
	if (!eventName) return null;
	for (const suffix of ["_IMPRESSION", "_CLICK"]) {
		if (eventName.endsWith(suffix)) {
			const prefix = eventName.slice(0, -suffix.length);
			if ((ALL_SURFACES as string[]).includes(prefix)) {
				return prefix as TrackableSurface;
			}
		}
	}
	return null;
};
